import React, { useState, useEffect } from 'react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';

const FieldSelector = ({ availableFields, initialSelectedFields, onFieldsChange }) => {
  const [selectedFields, setSelectedFields] = useState(initialSelectedFields || []);
  const [availableFieldsList, setAvailableFieldsList] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    // Filter out already selected fields from available fields
    const filteredAvailableFields = availableFields.filter(
      field => !selectedFields.includes(field) && !field.startsWith('---')
    );
    setAvailableFieldsList(filteredAvailableFields);
  }, [availableFields, selectedFields]);

  useEffect(() => {
    // Call the callback whenever selected fields change
    if (onFieldsChange) {
      onFieldsChange(selectedFields);
    }
  }, [selectedFields, onFieldsChange]);

  const handleAddField = (field) => {
    const newSelectedFields = [...selectedFields, field];
    setSelectedFields(newSelectedFields);
  };

  const handleRemoveField = (field) => {
    const newSelectedFields = selectedFields.filter(f => f !== field);
    setSelectedFields(newSelectedFields);
  };

  const handleDragEnd = (result) => {
    if (!result.destination) return;

    const items = Array.from(selectedFields);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    setSelectedFields(items);
  };

  const filteredAvailableFields = availableFieldsList.filter(
    field => field.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <div className="bg-gray-50 p-4 rounded-md border border-gray-200">
        <h3 className="text-lg font-medium text-gray-900 mb-3">Available Fields</h3>

        <div className="mb-4">
          <input
            type="text"
            placeholder="Search fields..."
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="max-h-96 overflow-y-auto">
          {filteredAvailableFields.map((field) => (
            <div
              key={field}
              className="flex items-center justify-between p-2 hover:bg-gray-100 rounded-md cursor-pointer mb-1 border border-gray-200"
              onClick={() => handleAddField(field)}
            >
              <span className="text-sm font-medium text-gray-700">{field}</span>
              <button
                type="button"
                className="ml-2 p-1 text-indigo-600 hover:text-indigo-900 rounded-full hover:bg-indigo-100"
                onClick={(e) => {
                  e.stopPropagation();
                  handleAddField(field);
                }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
              </button>
            </div>
          ))}
          {filteredAvailableFields.length === 0 && (
            <p className="text-sm text-gray-500 py-2">No matching fields found</p>
          )}
        </div>
      </div>

      <div className="bg-gray-50 p-4 rounded-md border border-gray-200">
        <h3 className="text-lg font-medium text-gray-900 mb-3">Selected Fields</h3>

        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="selected-fields">
            {(provided) => (
              <div
                {...provided.droppableProps}
                ref={provided.innerRef}
                className="max-h-96 overflow-y-auto"
              >
                {selectedFields.length === 0 ? (
                  <p className="text-sm text-gray-500 py-2">No fields selected</p>
                ) : (
                  selectedFields.map((field, index) => (
                    <Draggable key={field} draggableId={field} index={index}>
                      {(provided) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                          className="flex items-center justify-between p-2 bg-white rounded-md mb-2 border border-gray-200 shadow-sm"
                        >
                          <div className="flex items-center">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8h16M4 16h16" />
                            </svg>
                            <span className="text-sm font-medium text-gray-700">{field}</span>
                          </div>
                          <button
                            type="button"
                            className="ml-2 p-1 text-red-600 hover:text-red-900 rounded-full hover:bg-red-100"
                            onClick={() => handleRemoveField(field)}
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                      )}
                    </Draggable>
                  ))
                )}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>

        <div className="mt-4">
          <span className="text-sm text-gray-500">
            Drag and drop to reorder fields. {selectedFields.length} fields selected.
          </span>
        </div>
      </div>

      {/* Hidden input field to store the comma-separated list of headers */}
      <input
        type="hidden"
        id="report_template_headers"
        name="report_template[headers]"
        value={selectedFields.join(',')}
      />

      {/* Hidden input fields to store each value in an array */}
      {selectedFields.map((field, index) => (
        <input
          key={index}
          type="hidden"
          name="report_template[values][]"
          value={field}
        />
      ))}
    </div>
  );
};

export default FieldSelector;